import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { isEnvEnabled } from '~/lib/env';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useReleasesUrlParams } from '~/modules/artist-roster/hooks/useReleasesUrlParams';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';

import { DataUnavailable } from '../DataUnavailable/DataUnavailable';
import { TrackTable } from './TrackTable';

interface TrackListProps {
  uaid: number | null;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  setSortingState?: OnChangeFn<SortingState> | undefined;
}

export function TrackList({
  uaid,
  artistData,
  columnSort,
  setSortingState,
}: TrackListProps) {
  const { pageNumber, filters } = useReleasesUrlParams();
  const { t } = useTranslation('roster');
  const { searchText, releaseType, role } = filters;

  const isTrackPreReleasesEnabled = isEnvEnabled(
    'VITE_WMG_TRACK_PRE_RELEASES_ENABLED',
  );

  const {
    data: tracksData,
    error,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
  } = useArtistTracksQuery({
    uaid,
    columnSort,
    pageNumber,
    searchText,
    releaseType,
    isReleased: isTrackPreReleasesEnabled ? true : undefined,
    role,
  });

  if (!artistData) {
    return null;
  }

  if (error) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  const trackTableMeta = {
    artistName: artistData.name,
    filtersCount: filters?.releaseType !== undefined ? 1 : 0,
    filtersApplied: filters !== undefined,
    partyId: artistData.partyId,
    uaid,
  };

  return (
    <div className="relative px-xl pb-xl">
      <TrackTable
        data={tracksData?.items ?? []}
        meta={trackTableMeta}
        artistData={artistData}
        isLoadingTracks={isLoadingTracks}
        isFetchingTracks={isFetchingTracks}
        columnSort={columnSort}
        onSortingChange={setSortingState}
      />
    </div>
  );
}

TrackList.displayName = 'TrackList';
